
// File: src/pages/Login.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
// Using a custom button below for custom hover effects
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Mail, Lock, Sparkles } from 'lucide-react';
import logo from '/vixalor-logo.webp';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Set page title
  useEffect(() => {
    document.title = 'Vixalor POS - Login';
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setIsLoading(true);
  try {
    await login(email, password);
    navigate(from, { replace: true });
  } catch (err: any) {
    toast.error(err.message || 'Login failed');
    console.error('Login.tsx error:', err);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-4 py-10">
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,0.07),transparent_25%),radial-gradient(circle_at_80%_0%,rgba(59,130,246,0.05),transparent_22%),radial-gradient(circle_at_50%_80%,rgba(14,165,233,0.08),transparent_28%)]" />
      <div className="relative w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 bg-white shadow-2xl rounded-3xl overflow-hidden border border-slate-200">
        <div className="hidden lg:flex flex-col items-center justify-center bg-slate-100 p-12">
          <div className="w-full max-w-xs flex flex-col items-center gap-4">
            <img src={logo} alt="Vixalor" className="max-h-20 w-auto" />
            <p className="text-sm text-slate-500 text-center">
              Secure access to your point of sale and inventory.
            </p>
          </div>
        </div>

        <div className="p-8 lg:p-12">
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Vixalor" className="h-14 w-auto" />
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Vixalor POS</p>
                <p className="text-xl font-semibold text-slate-900">Welcome back</p>
              </div>
            </div>
          </div>

          <Card className="border-none shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-3xl font-semibold text-slate-900">Sign in</CardTitle>
              <CardDescription className="text-base text-slate-600">
                Enter your credentials to access your dashboard.
              </CardDescription>
            </CardHeader>

            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-6 px-0">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="username"
                      placeholder="you@example.com"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      disabled={isLoading}
                      required
                      className="pl-12 h-12 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-slate-900/80"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      disabled={isLoading}
                      required
                      className="pl-12 pr-14 h-12 rounded-xl border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-slate-900/80"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 focus:outline-none"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="px-0 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={[
                    'w-full h-12 rounded-xl font-semibold text-white tracking-wide',
                    'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900',
                    'shadow-[0_10px_30px_rgba(15,23,42,0.35)]',
                    'transition-all duration-200 ease-out',
                    'hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(15,23,42,0.4)]',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-900 focus-visible:ring-offset-white',
                    isLoading ? 'opacity-90 cursor-progress' : 'cursor-pointer'
                  ].join(' ')}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing In...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Sign In</span>
                      <Sparkles className="h-4 w-4" />
                    </div>
                  )}
                </button>
              </CardFooter>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
}
